/**
 * Sponsor Discovery model - explicit state definition.
 * 
 * Elm-ish architecture: State is explicit and typed.
 * No implicit state machines encoded in useState hooks.
 */
import type { FriendProfile, SponsorProfile } from '@/api/profiles';

/**
 * Discovery card shape - union type for both friend and sponsor profiles.
 * Uses identity-minimized profile types per safety doctrine.
 */
export type DiscoveryCard = FriendProfile | SponsorProfile;

/**
 * Possible actions a sponsor can take on a discovery card.
 */
export type SponsorDiscoveryAction = 'skip' | 'save' | 'requestIntro';

/**
 * Status values for the discovery program lifecycle.
 */
export type SponsorDiscoveryStatus =
  | 'idle'        // Initial state before any load
  | 'loading'     // Fetching profiles from API
  | 'ready'       // Profiles loaded and available for swiping
  | 'empty'       // No profiles available
  | 'error';     // Fetch failed with error message

/**
 * Complete state shape for the Sponsor Discovery program.
 * 
 * Security posture:
 * - All state is local to this program instance
 * - No sensitive identifiers stored (uses identity-minimized FriendProfile)
 * - Error messages are user-safe (no internal details exposed)
 */
export interface SponsorDiscoveryModel {
  /** Current status of the program */
  readonly status: SponsorDiscoveryStatus;
  /** List of available discovery cards */
  readonly cards: readonly DiscoveryCard[];
  /** Index of the currently active card (0-based) */
  readonly activeIndex: number;
  /** Error message when status is 'error' */
  readonly error: string | null;
  /** Action currently being processed (or null if idle) */
  readonly pendingAction: SponsorDiscoveryAction | null;
  /** Page size for pagination */
  readonly pageSize: number;
  /** Next offset for loading more cards */
  readonly nextOffset: number;
  /** Whether more pages exist */
  readonly hasMore: boolean;
  /** Set of profile IDs for which intro has been requested */
  readonly requestedIntros: ReadonlySet<string>;
  /** Card ID for optimistic intro request tracking (cleared on success/failure) */
  readonly optimisticIntroCardId: string | null;
}

/**
 * Initial/empty state for the discovery program.
 */
export const initialModel: SponsorDiscoveryModel = {
  status: 'idle',
  cards: [],
  activeIndex: 0,
  error: null,
  pendingAction: null,
  pageSize: 20,
  nextOffset: 0,
  hasMore: false,
  requestedIntros: new Set(),
  optimisticIntroCardId: null,
};

/**
 * Helper to check if there are more cards to show.
 */
export function hasMoreCards(model: SponsorDiscoveryModel): boolean {
  return model.status === 'ready' && model.activeIndex < model.cards.length;
}

/**
 * Helper to get the current active card.
 */
export function getActiveCard(model: SponsorDiscoveryModel): DiscoveryCard | null {
  if (model.status !== 'ready') return null;
  if (model.activeIndex >= model.cards.length) return null;
  return model.cards[model.activeIndex];
}

/**
 * Helper to check if the program is in a terminal state.
 */
export function isTerminalState(model: SponsorDiscoveryModel): boolean {
  return model.status === 'empty' || model.status === 'error';
}

/**
 * Parameters for canRequestIntro check.
 * 
 * Designed to be explicit about what conditions enable/disable the intro action.
 * Does NOT consider:
 * - remaining profile count (queue status)
 * - profile location or optional fields
 * - profile completeness
 */
export interface CanRequestIntroParams {
  /** Whether the current viewer is a sponsor account */
  readonly viewerIsSponsor: boolean;
  /** Whether the current viewer is a friend account */
  readonly viewerIsFriend: boolean;
  /** The current active profile card (or null if none) */
  readonly profile: DiscoveryCard | null;
  /** Set of profile IDs that have already had intro requested */
  readonly requestedIntros: ReadonlySet<string>;
  /** Whether ANY action (skip/save/intro) is currently pending */
  readonly hasPendingAction: boolean;
}

/**
 * Reason why intro cannot be requested.
 */
export type IntroDisabledReason =
  | { readonly reason: 'viewer-is-friend' }
  | { readonly reason: 'no-profile' }
  | { readonly reason: 'already-requested' }
  | { readonly reason: 'submitting' }
  | null;

/**
 * Check if a sponsor can request an intro for the current card.
 * 
 * Intro is enabled when:
 * - viewer is sponsor
 * - current visible profile exists
 * - intro has not already been requested for this profile
 * - request is not currently submitting
 * 
 * Intro is disabled when:
 * - viewer is friend
 * - no current profile exists
 * - duplicate intro/request exists
 * - request is currently submitting
 */
export function canRequestIntro(params: CanRequestIntroParams): {
  readonly canRequest: boolean;
  readonly disabledReason: IntroDisabledReason;
} {
  // Friend accounts cannot request intros (server-side enforced)
  if (params.viewerIsFriend) {
    return { canRequest: false, disabledReason: { reason: 'viewer-is-friend' } };
  }
  
  // Sponsor accounts only beyond this point
  if (!params.viewerIsSponsor) {
    return { canRequest: false, disabledReason: { reason: 'viewer-is-friend' } };
  }
  
  // No current profile = no action possible
  if (!params.profile) {
    return { canRequest: false, disabledReason: { reason: 'no-profile' } };
  }
  
  // Already requested this profile's intro
  if (params.requestedIntros.has(params.profile.id)) {
    return { canRequest: false, disabledReason: { reason: 'already-requested' } };
  }
  
  // Any action is pending (skip/save/intro) - disable to prevent race conditions
  if (params.hasPendingAction) {
    return { canRequest: false, disabledReason: { reason: 'submitting' } };
  }
  
  // All conditions met - intro can be requested
  return { canRequest: true, disabledReason: null };
}

/**
 * Get human-readable text for an intro disabled reason.
 */
export function getIntroDisabledReasonText(reason: IntroDisabledReason): string | null {
  switch (reason?.reason) {
    case 'viewer-is-friend':
      return 'Sponsor accounts only';
    case 'no-profile':
      return null; // No profile shown, button likely absent
    case 'already-requested':
      return 'Already requested';
    case 'submitting':
      return 'Sending request...';
    case null:
      return null;
  }
}
